'use client'

import Link from 'next/link'
import { memo } from 'react'
import { cn } from '@/utils/twMerge'
import type { CatalogItem, MarketplaceProduct, PricingSegment } from '@/types/marketplace'
import { formatPrice, getSegmentDisplayName, getHeadlineSegment } from '@/utils/microsoftFormatters'
import { MICROSOFT_CATEGORIES_MAP, SEGMENT_CONFIG_MAP } from '@/constants/microsoftCategories'
import { GoogleLogoTile, MicrosoftLogoTile } from './MicrosoftLogo'
import { useQuoteCart } from './QuoteCart'

/** 'licensed' needs the app's per-user license data, which the marketing site has no session for, so it renders nothing yet */
export type CatalogCardStatus = 'in-cart' | 'licensed' | null

interface ProductCardProps {
  product: CatalogItem | MarketplaceProduct
  customClassName?: string
  /** When set, show the segment-specific SKU title and link to the segment subpage */
  activeSegment?: PricingSegment
  /** Overrides the state the quote cart implies */
  cardStatus?: CatalogCardStatus
  headingLevel?: 2 | 3 | 4
}

export const ProductCard = memo(
  function ProductCard({
    product,
    customClassName = 'bg-white',
    activeSegment,
    cardStatus,
    headingLevel = 3,
  }: ProductCardProps) {
    const { items } = useQuoteCart()

    const pinned = activeSegment ? product.segments.find((s) => s.segment === activeSegment) : undefined
    const cardSegment = pinned ?? getHeadlineSegment(product as MarketplaceProduct) ?? product.segments[0]
    const price = cardSegment?.erpMonthly ?? null
    const displayTitle = pinned ? getSegmentDisplayName(product, pinned.segment) : product.displayName
    const segmentSlug = pinned && 'segmentSlug' in pinned ? pinned.segmentSlug : null
    const isGoogleWorkspace = product.category === 'google-workspace'
    // The /<slug>/<segment> route only redirects to the segment's own page, so link there directly
    const detailHref = isGoogleWorkspace
      ? `/marketplace/google-workspace/${product.slug}`
      : pinned
        ? `/marketplace/microsoft/${segmentSlug ?? `${product.slug}/${pinned.segment}`}`
        : `/marketplace/microsoft/${product.slug}`

    const inCart = items.some((i) => i.productSlug === product.slug && (!pinned || i.segment === pinned.segment))
    const status = cardStatus !== undefined ? cardStatus : inCart ? 'in-cart' : null
    const Heading = `h${headingLevel}` as const

    return (
      <article
        className={cn(
          'relative flex flex-col gap-4 rounded-lg border border-primary-ink-10 p-6 transition-colors hover:border-primary-ink-30 motion-reduce:transition-none',
          customClassName,
          status === 'in-cart' && 'border-primary-ink-100 hover:border-primary-ink-100',
        )}>
        <div className="flex items-start justify-between gap-4">
          {isGoogleWorkspace ? <GoogleLogoTile /> : <MicrosoftLogoTile />}
          {status === 'in-cart' ? (
            <span className="inline-flex h-5 items-center gap-1 whitespace-nowrap rounded bg-primary-ink-60 px-1 text-xs font-medium leading-[18px] tracking-[-0.1px] text-white">
              <svg
                aria-hidden="true"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                viewBox="0 0 24 24">
                <path d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
              </svg>
              In cart
            </span>
          ) : product.category === 'google-workspace' ? null : (
            <span className="inline-flex h-5 items-center whitespace-nowrap rounded bg-primary-ink-6 px-1 text-xs font-medium leading-[18px] tracking-[-0.1px] text-primary-ink-85">
              {MICROSOFT_CATEGORIES_MAP[product.category]?.shortName}
            </span>
          )}
        </div>

        <div>
          {cardSegment && (
            <p className="text-[13px] font-medium uppercase leading-5 tracking-[0.5px] text-primary-ink-85">
              {SEGMENT_CONFIG_MAP[cardSegment.segment]?.shortLabel}
            </p>
          )}
          <Heading
            title={displayTitle}
            className="line-clamp-2 min-h-12 text-lg font-medium leading-6 tracking-[-0.3px] text-primary-ink-100">
            {/* Stretched over the card so the whole card is one click target */}
            <Link href={detailHref} className="after:absolute after:inset-0 after:rounded-lg after:content-['']">
              {displayTitle}
            </Link>
          </Heading>
        </div>

        <div>
          <p className="text-xs leading-[18px] tracking-[-0.15px] text-primary-ink-60">Starting from</p>
          <p className="flex flex-wrap items-end gap-x-1">
            <span className="text-[30px] font-medium leading-[38px] tracking-[-1px] text-primary-ink-100">
              {formatPrice(price)}
            </span>
            {price ? (
              <span className="pb-1 text-sm leading-5 tracking-[-0.35px] text-primary-ink-100">per month</span>
            ) : null}
          </p>
        </div>

        {/* Title and description always take two lines' worth of space so every card's price and button line up */}
        <p className="line-clamp-2 min-h-9 text-xs leading-[18px] tracking-[-0.15px] text-primary-ink-100">
          {product.description}
        </p>

        <Link
          href={detailHref}
          aria-label={`View details: ${displayTitle}`}
          className="relative z-10 mt-auto flex h-8 w-full items-center justify-center rounded-md border border-primary-ink-10 bg-white px-3 text-sm font-medium tracking-[-0.25px] text-primary-ink-100 transition-colors hover:border-primary-ink-30 motion-reduce:transition-none">
          View details
        </Link>
      </article>
    )
  },
  (prev, next) =>
    prev.product.slug === next.product.slug &&
    prev.activeSegment === next.activeSegment &&
    prev.cardStatus === next.cardStatus &&
    prev.headingLevel === next.headingLevel &&
    prev.customClassName === next.customClassName,
)
